import React, { useCallback, useEffect, useState } from 'react';

import copy from 'copy-to-clipboard';

import { toast } from 'react-toastify';

import { Button } from '@cloudez/cloudez-design-system';

import {
  faCopy,
  faEye,
  faEyeSlash,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import exclamation from 'assets/img/svg/exclamation.svg';

import { useLayout } from 'hooks/layout';

import { CustomInput } from 'components/NewComponents';

import { deleteAuthTokenService, getAuthTokenService } from 'services/auth';

import RevokeModal from './RevokeModal';

import { Container, ErrorBox, Text, Title, TokenBox } from './styles';
import { Alert } from 'components/Alert';

const Token: React.FC = () => {
  const theme = useLayout();

  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [revokeModal, setRevokeModal] = useState(false);

  const getToken = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await getAuthTokenService();
      const _token = data?.token ?? data?.key ?? data?.access ?? data;

      if (typeof _token === 'string' && _token) {
        setToken(_token);
      } else {
        setError('Não foi possível ler o token retornado pela API.');
      }
    } catch (e) {
      setError('Não foi possível carregar seu token. Tente novamente.');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    getToken();
  }, [getToken]);

  const copyToken = useCallback(() => {
    copy(token);
    toast.success('Token copiado.');
  }, [token]);

  const revokeToken = useCallback(async () => {
    try {
      await deleteAuthTokenService();
      toast.success('Token revogado. Um novo token foi gerado.');
    } catch (e) {
      toast.error('Não foi possível revogar seu token.');
    }

    setRevealed(false);
    await getToken();
  }, [getToken]);

  return (
    <Container>
      <Title>Token de API</Title>
      <Text>
        Use este token para autenticar suas requisições à API da{' '}
        {theme.slug === 'configr' ? 'Configr' : 'Cloudez'}.
      </Text>
      <Alert info style={{ margin: '0 50px 10px 0' }}>
        <img alt="exclamation" src={exclamation} />
        <span>
          Mantenha-o seguro e não o compartilhe, pois ele permite realizar ações
          em sua conta.
        </span>
      </Alert>
      {error ? (
        <ErrorBox>
          <span>{error}</span>
          <Button outline onClick={getToken} disabled={loading}>
            Tentar novamente
          </Button>
        </ErrorBox>
      ) : (
        <TokenBox loading={loading}>
          <CustomInput
            name="token"
            block
            readOnly
            type={revealed ? 'text' : 'password'}
            value={loading ? '' : token}
            placeholder={loading ? 'Carregando token...' : ''}
            icon={
              loading
                ? [{ svg: faSpinner, isRight: true }]
                : [
                    {
                      svg: revealed ? faEyeSlash : faEye,
                      isRight: true,
                      action: () => setRevealed(!revealed),
                    },
                    {
                      svg: faCopy,
                      isRight: true,
                      action: copyToken,
                    },
                  ]
            }
          />
        </TokenBox>
      )}

      {!error && (
        <div>
          <Button
            error
            outline
            disabled={loading}
            onClick={() => setRevokeModal(true)}
          >
            Revogar token
          </Button>
        </div>
      )}

      {revokeModal && (
        <RevokeModal
          show={revokeModal}
          setShow={setRevokeModal}
          action={revokeToken}
        />
      )}
    </Container>
  );
};

export default Token;
